/**
 * @module tracking/handTracker
 * Initialises the MediaPipe Tasks Vision HandLandmarker and processes
 * each video frame to produce normalised landmark positions.
 *
 * Uses `runningMode: "VIDEO"` for real-time streaming and supports up to
 * two simultaneous hands (two-blade gameplay).
 *
 * MediaPipe WASM + model are lazy-loaded after the page becomes interactive;
 * call `initHandTracker` once, await it, then call `detectFrame` each
 * tracking loop iteration.
 */

import type { HandLandmarkerResult } from '@mediapipe/tasks-vision';
import { WASM_BASE, MODEL_URL } from './trackerConstants.js';
import { CONFIG } from '../game-config.js';

// ---------------------------------------------------------------------------
// Module-level state (singleton tracker)
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _landmarker: any = null;
let _ready = false;

// ---------------------------------------------------------------------------
// Public functional API (per module-contracts.md)
// ---------------------------------------------------------------------------

/** Optional tracker tuning (set once at init). */
export interface HandTrackerOptions {
  /**
   * Max simultaneous hands. Desktop default 2 (two-blade gameplay);
   * mobile passes 1 — halves per-frame inference cost on phone GPUs.
   */
  numHands?: number;
  /** Palm detection (initial acquisition) confidence. Default 0.5. */
  minHandDetectionConfidence?: number;
  /**
   * Hand presence confidence. LOWER than the 0.5 default keeps the tracker
   * on the fast (cheap) tracking path for motion-blurred hands instead of
   * re-running palm detection. Default 0.5.
   */
  minHandPresenceConfidence?: number;
  /**
   * Landmark tracking confidence. LOWER than the 0.5 default holds the lock
   * on fast-moving hands — losing it makes the cursor blink out mid-slash.
   * Default 0.5.
   */
  minTrackingConfidence?: number;
}

/**
 * Lazy-initialises MediaPipe HandLandmarker (VIDEO mode, GPU delegate with
 * CPU fallback). Resolves only after the WASM + model are fully loaded.
 * Safe to call multiple times — no-ops after the first successful init.
 *
 * @param onProgress - Optional load progress callback 0.0–1.0.
 * @param options - Optional tracker tuning ({@link HandTrackerOptions}).
 */
export async function initHandTracker(
  onProgress?: (ratio: number) => void,
  options?: HandTrackerOptions,
): Promise<void> {
  if (_ready) return;

  const numHands = options?.numHands ?? 2;
  const minHandDetectionConfidence = options?.minHandDetectionConfidence ?? 0.5;
  const minHandPresenceConfidence = options?.minHandPresenceConfidence ?? 0.5;
  const minTrackingConfidence = options?.minTrackingConfidence ?? 0.5;

  // Signal start of load.
  onProgress?.(0);

  // Dynamic import keeps MediaPipe out of the initial bundle.
  const { HandLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');

  onProgress?.(0.3);

  const vision = await FilesetResolver.forVisionTasks(WASM_BASE);

  onProgress?.(0.6);

  // Try GPU delegate first; fall back to CPU on failure (ADR-005).
  try {
    _landmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numHands,
      minHandDetectionConfidence,
      minHandPresenceConfidence,
      minTrackingConfidence,
    });
  } catch {
    // GPU delegate unavailable — fall back to CPU.
    _landmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'CPU',
      },
      runningMode: 'VIDEO',
      numHands,
      minHandDetectionConfidence,
      minHandPresenceConfidence,
      minTrackingConfidence,
    });
  }

  _ready = true;
  onProgress?.(1);
}

/**
 * Process one video frame. Must be called in the tracking loop (NOT rAF).
 * `timestamp` should be `performance.now()` at the moment of the call.
 * Returns null if the tracker is not yet initialised.
 */
let _consecutiveFailures = 0;

/**
 * Last timestamp fed to the graph. MediaPipe VIDEO mode requires STRICTLY
 * monotonically increasing integer timestamps — equal or backwards values
 * latch the calculator graph into a permanent INVALID_ARGUMENT error state.
 */
let _lastTimestamp = 0;

export function detectFrame(
  videoEl: HTMLVideoElement,
  timestamp: number,
): HandLandmarkerResult | null {
  if (!_ready || !_landmarker) return null;

  // Enforce strict monotonicity regardless of caller timing (multiple rAF
  // chains, float rounding collisions, clock adjustments).
  const ts = Math.max(Math.floor(timestamp), _lastTimestamp + 1);
  _lastTimestamp = ts;

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const result = _landmarker.detectForVideo(videoEl, ts) as HandLandmarkerResult;
    _consecutiveFailures = 0;
    return result;
  } catch (err: unknown) {
    // A failed WASM graph throws on every subsequent frame — do not let it
    // flood the console or crash the loop. After repeated failures, disable
    // the tracker entirely; the game remains playable via pointer fallback.
    _consecutiveFailures += 1;
    if (_consecutiveFailures === 1) {
      console.warn('[Fruit Slasher] Hand tracking frame failed — retrying.', err);
    }
    if (_consecutiveFailures >= CONFIG.tracking.failover.maxConsecutiveFailures) {
      console.warn(
        `[Fruit Slasher] Hand tracking disabled after ${CONFIG.tracking.failover.maxConsecutiveFailures} consecutive failures — pointer fallback active.`,
      );
      destroyHandTracker();
    }
    return null;
  }
}

/** True after `initHandTracker()` resolves. */
export function isReady(): boolean {
  return _ready;
}

/** Teardown — releases WASM resources. Call when the tracking session ends. */
export function destroyHandTracker(): void {
  if (_landmarker) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    _landmarker.close();
    _landmarker = null;
    _ready = false;
  }
}

// ---------------------------------------------------------------------------
// Legacy class-based API (kept for main.ts PoC backwards-compat)
// ---------------------------------------------------------------------------

/** Normalised [0–1] coordinate pair from MediaPipe. */
export interface NormPoint {
  x: number;
  y: number;
  z: number;
}

/** One hand's worth of landmarks — 21 points in MediaPipe order. */
export type HandLandmarks = NormPoint[];

/** Callback invoked once per processed video frame. */
export type TrackingCallback = (hands: HandLandmarks[]) => void;

/**
 * @deprecated Use the functional API (`initHandTracker`, `detectFrame`, etc.).
 * Kept for backwards-compat with the PoC `main.ts`.
 */
export class HandTracker {
  get ready(): boolean {
    return isReady();
  }

  async init(wasmBasePath: string): Promise<void> {
    // wasmBasePath ignored — module uses pinned CDN URL.
    void wasmBasePath;
    await initHandTracker();
  }

  processFrame(
    video: HTMLVideoElement,
    timestampMs: number,
    callback: TrackingCallback,
  ): void {
    const result = detectFrame(video, timestampMs);
    if (!result) {
      callback([]);
      return;
    }
    callback((result.landmarks ?? []));
  }

  close(): void {
    destroyHandTracker();
  }
}
